/**
 * Text-only result banner: event + description + RBI count + close X.
 * Sits in GameView's `.center` column ABOVE the FieldDiamond SVG so the
 * outfielder badges and the diamond proper remain unobscured.
 *
 * Visibility driven by `useAtBatResultLifecycle` (8s + dismissed + replay).
 * Close button calls `onDismiss(atBatIndex)`; replay is wired from GameView.
 */
import type { LastAtBatResult } from '../state/selectors';
import { useAtBatResultLifecycle } from '../hooks/useAtBatResultLifecycle';
import styles from './AtBatResultBanner.module.css';

interface AtBatResultBannerProps {
  result: LastAtBatResult;
  dismissedAtBatIndex: number | null;
  replayKey: number;
  onDismiss: (atBatIndex: number) => void;
}

export default function AtBatResultBanner({
  result,
  dismissedAtBatIndex,
  replayKey,
  onDismiss,
}: AtBatResultBannerProps): JSX.Element {
  const { visible } = useAtBatResultLifecycle({
    atBatIndex: result.atBatIndex,
    dismissedAtBatIndex,
    replayKey,
  });

  const rbis = result.runners.filter((r) => r.isScoringEvent).length;
  const outs = result.runners.filter((r) => r.isOut).length;

  return (
    <div
      className={`${styles.bannerSlot} ${visible ? styles.bannerVisible : styles.bannerHidden}`}
      aria-hidden={!visible}
      data-testid="at-bat-result-banner"
      data-at-bat-index={result.atBatIndex}
    >
      <div className={styles.banner} role="status" aria-live="polite">
        <span className={styles.event}>{result.event}</span>
        {rbis > 0 && (
          <span className={`${styles.tag} ${styles.tagScore}`}>
            {rbis === 1 ? '1 RBI' : `${rbis} RBI`}
          </span>
        )}
        {outs > 0 && (
          <span className={`${styles.tag} ${styles.tagOut}`}>
            {outs === 1 ? '1 OUT' : `${outs} OUTS`}
          </span>
        )}
        {result.description && (
          <span className={styles.desc} title={result.description}>
            {result.description}
          </span>
        )}
        <button
          type="button"
          className={styles.close}
          onClick={() => onDismiss(result.atBatIndex)}
          aria-label="Dismiss at-bat result"
          data-testid="at-bat-result-banner-close"
        >
          ×
        </button>
      </div>
    </div>
  );
}
